/**
 * AgentChatLayout — Ivy Chat interface powered by @assistant-ui/react.
 *
 * Auth flow:
 *   1. Check if we already have an ivy-chat session token (localStorage)
 *   2. If not, get Firebase ID token from web-ui's existing auth
 *   3. Exchange it with ivy-chat backend for a session token
 *   4. Store session token → all API calls use it automatically
 */

import { useEffect, useState, useCallback } from 'react';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { useIvyRuntime } from '@/lib/ivyChat/useIvyRuntime';
import { useIvyChatStore, getPersistedActiveSession } from '@/stores/ivyChatStore';
import { IvyThread } from './IvyThread';
import { IvySidebar } from './IvySidebar';
import { useIvyChatAuth } from '@/hooks/useIvyChatAuth';
import { ClaudeTokenModal } from './ClaudeTokenModal';
import { Wand2, Settings } from 'lucide-react';
import * as ivyChatApi from '@/services/ivyChatApi';

export function AgentChatLayout() {
  const { authReady, authError } = useIvyChatAuth();

  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<{ hasToken: boolean; hint: string | null } | null>(null);
  const [platformFunded, setPlatformFunded] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);

  const checkTokenStatus = useCallback(async () => {
    setPlatformFunded(await ivyChatApi.isPlatformFunded());
    const providerStatus = await ivyChatApi.getProviderStatus();
    const activeP = providerStatus.providers[providerStatus.activeProvider];
    setTokenStatus({
      hasToken: providerStatus.hasAnyKey,
      hint: activeP?.hint || null,
    });
    setTokenChecked(true);
  }, []);

  useEffect(() => {
    if (authReady) checkTokenStatus();
  }, [authReady, checkTokenStatus]);

  // Once authenticated, load sessions
  const loadSessions = useIvyChatStore(s => s.loadSessions);
  const selectSession = useIvyChatStore(s => s.selectSession);
  const checkAndReconnect = useIvyChatStore(s => s.checkAndReconnect);

  useEffect(() => {
    if (!authReady) return;

    async function init() {
      await loadSessions();

      // Only restore persisted session if it still exists on the server
      const persistedChatId = getPersistedActiveSession();
      const sessions = useIvyChatStore.getState().sessions;
      if (persistedChatId && sessions.some(s => s.chatId === persistedChatId)) {
        await selectSession(persistedChatId);
        await checkAndReconnect(persistedChatId);
      } else if (persistedChatId) {
        // Stale session — clear it
        localStorage.removeItem('ivy_chat_active_session');
      }
    }
    init();
  }, [authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show auth error
  if (authError) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-foreground">
        <div className="text-center max-w-md px-6">
          <p className="text-lg font-medium mb-2">Authentication Required</p>
          <p className="text-sm text-muted-foreground mb-4">{authError}</p>
          <a href="/login?redirect=/agent-chat" className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors">
            Sign In
          </a>
        </div>
      </div>
    );
  }

  if (!authReady || !tokenChecked) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  if (!tokenStatus?.hasToken && !platformFunded) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-screen bg-background text-foreground gap-3">
          <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-brand" />
          </div>
          <p className="text-sm font-medium text-foreground">Add your model key</p>
          <p className="text-xs text-muted-foreground max-w-[280px] text-center">
            This chat runs on your own model key. It is encrypted and stored securely.
          </p>
          <button
            onClick={() => setShowTokenModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Settings className="w-3 h-3" />
            Configure
          </button>
        </div>
        <ClaudeTokenModal
          open={showTokenModal}
          onClose={() => {
            setShowTokenModal(false);
            checkTokenStatus();
          }}
        />
      </>
    );
  }

  return (
    <>
      <AuthenticatedChat tokenHint={tokenStatus?.hint ?? null} onOpenSettings={() => setShowTokenModal(true)} />
      <ClaudeTokenModal
        open={showTokenModal}
        onClose={() => {
          setShowTokenModal(false);
          checkTokenStatus();
        }}
      />
    </>
  );
}

function AuthenticatedChat({ tokenHint, onOpenSettings }: { tokenHint: string | null; onOpenSettings: () => void }) {
  const runtime = useIvyRuntime();

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <div className="flex h-screen bg-background text-foreground overflow-hidden">
        <IvySidebar onOpenSettings={onOpenSettings} />
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          <IvyThread />
          <div className="flex-shrink-0 flex items-center justify-between px-3 py-1 border-t border-border">
            <span className="text-[10px] text-muted-foreground/50 truncate">
              {tokenHint ? 'Your own key' : ''}
            </span>
            <button
              onClick={onOpenSettings}
              className="p-1 rounded text-muted-foreground/40 hover:text-foreground hover:bg-muted transition-colors"
              title="Model key settings"
            >
              <Settings className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </AssistantRuntimeProvider>
  );
}
